/*
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Highlight from '../gfx/Highlight';
import Fadeable from '../gfx/Fadeable';

import vertexShader from '../shaders/ARModelDimensions/ARModelDimensions.vert';
import fragmentShader from '../shaders/ARModelDimensions/ARModelDimensions.frag';

import * as Constants from '../constants/Constants';

import Pill from '../gfx/Pill';
import Label from '../gfx/Label';
import { font16, font24, font32, font48, font64 } from '../fonts/fonts';

let tempWorldPos = new THREE.Vector3();
let tempWorldQuat = new THREE.Quaternion();

export default class ARModelDimensions extends Fadeable {
  constructor(props) {
    props = props ? props : {};
    props = Object.assign(
      {
        label: {
          scale: 0.00125,
          text: 'Width',
          color: new THREE.Color(0xff0000),
          alpha: 0.75,
          font: font32,
          padding: 0,
        },
        pill: {
          color: new THREE.Color(0xff00ff),
          alpha: 0.75,
        },
      },
      props
    );
    super(props);
    this.setup(props);
  }

  setup = props => {
    this.setupProperties(props);
    this.setupHighlight(props);
    this.setupDimensions(props);
    this.setupEvents();
  };

  setupProperties = props => {
    this.model = props.model;
    this.padding = props.padding != undefined ? props.padding : 0.1;
    this.bb = props.boundingBox;
    this.size = new THREE.Vector3();
    this.bb.getSize(this.size);
    this.controls = props.controls;
    this.duration = props.duration != undefined
      ? props.duration
      : Constants.duration;
  };

  setupHighlight = props => {
    let geometry = new THREE.PlaneBufferGeometry(
      this.size.x + this.padding,
      this.size.z + this.padding
    );
    geometry.applyMatrix(
      new THREE.Matrix4().makeRotationX(THREE.Math.degToRad(-90))
    );

    this.highlight = new THREE.Mesh(
      geometry,
      new THREE.RawShaderMaterial({
        uniforms: {
          aspect: {
            value: this.size.x / this.size.z,
          },
          radius: {
            value: props.radius != undefined ? props.radius : 0.2,
          },
          thickness: {
            value: props.padding,
          },
          color: {
            value: props.color != undefined
              ? props.color
              : new THREE.Color(0xffffff),
          },
          alpha: this.alpha,
          opacity: {
            value: props.opacity != undefined ? props.opacity : 1.0,
          },
        },
        vertexShader,
        fragmentShader,
        transparent: true,
        depthWrite: false,
      })
    );
    this.model.add(this.highlight);
  };

  setupDimensions = props => {
    this.setupWidthLabel(props);
    this.setupDepthLabel(props);
    this.setupHeightLabel(props);
  };

  setupWidthLabel = props => {
    this.widthLabel = new Label({
      text: 'Width: ' + Number(this.size.x * 39.3701).toFixed(2) + ' in',
      color: new THREE.Color(0xffffff),
      alpha: 0.75,
      font: font32,
    });

    let layout = this.widthLabel.getLayout();
    let placer = new THREE.Object3D();

    let scale = props.label.scale; //props.label.scale;
    placer.scale.set(scale, scale, scale);
    placer.rotation.set(Math.PI / 2.0, 0.0, 0.0);
    placer.position.set(
      -this.size.x / 2.0,
      0.0,
      this.padding + scale * layout._capHeight + this.size.z / 2.0
    );
    placer.add(this.widthLabel);
    this.highlight.add(placer);
  };

  setupDepthLabel = props => {
    this.depthLabel = new Label({
      text: 'Depth: ' + Number(this.size.z * 39.3701).toFixed(2) + ' in',
      color: new THREE.Color(0xffffff),
      alpha: 0.75,
      font: font32,
    });

    let layout = this.depthLabel.getLayout();
    let placer = new THREE.Object3D();

    let scale = props.label.scale;
    placer.scale.set(scale, scale, scale);
    placer.rotation.set(Math.PI * 0.5, 0.0, Math.PI * 0.5);
    placer.position.set(
      -this.padding - scale * layout._capHeight - this.size.x * 0.5,
      0.0,
      -this.size.z * 0.5
    );
    placer.add(this.depthLabel);
    this.highlight.add(placer);
  };

  setupHeightLabel = props => {
    this.heightLabel = new Label({
      text: 'Height: ' + Number(this.size.y * 39.3701).toFixed(2) + ' in',
      color: new THREE.Color(0xffffff),
      alpha: 0.75,
      font: font32,
    });

    let layout = this.heightLabel.getLayout();
    let placer = new THREE.Object3D();

    let scale = props.label.scale; //props.label.scale;
    placer.scale.set(scale, scale, scale);
    placer.rotation.set(0.0, Math.PI * 0.5, Math.PI * 0.5);
    placer.position.set(
      this.padding + scale * layout._capHeight + this.size.x * 0.5,
      0.0,
      this.size.z * 0.5
    );
    placer.add(this.heightLabel);
    this.highlight.add(placer);
  };

  setupEvents = () => {
    this.controls.addEventListener('down', () => {
      this.fadeIn(this.duration);
      this.widthLabel.fadeIn(this.duration);
      this.depthLabel.fadeIn(this.duration);
      this.heightLabel.fadeIn(this.duration);
    });

    this.controls.addEventListener('up', () => {
      this.fadeOut(this.duration);
      this.widthLabel.fadeOut(this.duration);
      this.depthLabel.fadeOut(this.duration);
      this.heightLabel.fadeOut(this.duration);
    });
  };
}



// WEBPACK FOOTER //
// ./src/ar/ARModelDimensions.js